import { LogLevelBadge } from "@/components/system-logs/LogLevelBadge";
import { LogStatusBadge } from "@/components/system-logs/LogStatusBadge";
import type { SystemLogRow } from "@/hooks/use-system-logs";

const CATEGORY_LABEL: Record<SystemLogRow["category"], string> = {
  request: "Request",
  error: "Error",
  app: "App",
};

function formatEventTime(iso: string): { time: string; date: string } {
  const d = new Date(iso);
  const time = d.toLocaleTimeString(undefined, {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const date = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  return { time, date };
}

function truncateActor(actorUserId: string | null): string {
  if (!actorUserId) return "—";
  return actorUserId.length > 10 ? `${actorUserId.slice(0, 8)}…` : actorUserId;
}

function LogTableRow({ row, onClick }: { row: SystemLogRow; onClick: (id: string) => void }) {
  const { time, date } = formatEventTime(row.eventAt);
  return (
    <tr
      className="cursor-pointer border-b border-border/50 transition-colors hover:bg-muted/40"
      onClick={() => onClick(row.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick(row.id);
        }
      }}
      tabIndex={0}
      aria-label={`Log entry: ${row.message}`}
    >
      <td className="px-3 py-2.5 align-top">
        <div className="font-mono text-sm">{time}</div>
        <div className="text-xs text-muted-foreground">{date}</div>
      </td>
      <td className="px-3 py-2.5 align-top">
        <LogLevelBadge level={row.level} />
      </td>
      <td className="px-3 py-2.5 align-top text-sm text-muted-foreground">
        {CATEGORY_LABEL[row.category]}
      </td>
      <td className="px-3 py-2.5 align-top">
        <div className="flex items-baseline gap-1.5">
          {row.method && <span className="font-mono text-sm font-semibold">{row.method}</span>}
          <span className="truncate font-mono text-sm text-muted-foreground">
            {row.path ?? "—"}
          </span>
        </div>
        <div className="mt-0.5 truncate text-sm text-muted-foreground">{row.message}</div>
      </td>
      <td className="px-3 py-2.5 align-top">
        <LogStatusBadge status={row.status} />
      </td>
      <td className="px-3 py-2.5 align-top text-sm tabular-nums text-muted-foreground">
        {row.durationMs != null ? `${row.durationMs}ms` : "—"}
      </td>
      <td
        className="px-3 py-2.5 align-top font-mono text-sm text-muted-foreground"
        title={row.actorUserId ?? undefined}
      >
        {truncateActor(row.actorUserId)}
      </td>
    </tr>
  );
}

export function LogsTable({
  rows,
  onRowClick,
}: {
  rows: SystemLogRow[];
  onRowClick: (id: string) => void;
}) {
  return (
    <div className="overflow-hidden rounded-md border border-border bg-card">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-3 py-2 font-semibold">Time</th>
            <th className="px-3 py-2 font-semibold">Level</th>
            <th className="px-3 py-2 font-semibold">Category</th>
            <th className="px-3 py-2 font-semibold">Method &amp; Path</th>
            <th className="px-3 py-2 font-semibold">Status</th>
            <th className="px-3 py-2 font-semibold">Duration</th>
            <th className="px-3 py-2 font-semibold">Actor</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <LogTableRow key={row.id} row={row} onClick={onRowClick} />
          ))}
        </tbody>
      </table>
    </div>
  );
}
